
















































import { PopoverAnchor } from 'types'
import { Component, Vue, Prop } from 'vue-property-decorator'

/**
 * Utility function for helping to calculate popover anchor
 */
export function calculatePopoverAnchor(
  pointerEvent: PointerEvent,
  position: PopoverAnchor['position'],
  popoverAddon?: { top: number; left: number; width: number; height: number },
): PopoverAnchor {
  const targetRect = (pointerEvent.target as HTMLElement).getBoundingClientRect()

  const addonTop = popoverAddon?.top ?? 0
  const addonLeft = popoverAddon?.left ?? 0
  const addonWidth = popoverAddon?.width ?? 0
  const addonHeight = popoverAddon?.height ?? 0

  return {
    top: targetRect.top + addonTop,
    left: targetRect.left + addonLeft,
    width: targetRect.width + addonWidth,
    height: targetRect.height + addonHeight,
    position,
  }
}

// Top edge margin, used only in "top" variant to detect overflow.
const TOP_EDGE_MARGIN = 24

@Component({})
export default class OzRichPopoverModal extends Vue {
  $refs!: {
    popover: HTMLDivElement
  }
  @Prop({ default: () => [] }) headerClasses!: string[]
  @Prop({ default: true }) xHeader!: boolean
  @Prop({ required: true }) windowHeight!: number
  @Prop({ required: true }) windowWidth!: number
  @Prop({ default: 'default' }) popoverWidth!: 'default' | 'wide' | number | 'auto'
  @Prop({ required: true }) popoverAnchor!: {
    position?: 'top' | 'side'
    top: number
    left: number
    width: number
    height: number
  }
  @Prop({ default: () => [] }) popoverClasses!: string[]
  @Prop({ default: () => [] }) popoverBgClasses!: string[]
  @Prop({ default: () => [] }) popoverTipClasses!: string[]
  @Prop({ default: () => [] }) popoverTipPathClasses!: string[]
  @Prop({ default: 'ltr' }) dir!: 'ltr' | 'rtl'

  get anchorDistanceToBottom(): number {
    return this.windowHeight - this.popoverAnchor.top
  }

  get isRtl(): boolean {
    return this.dir === 'rtl'
  }

  autoCalculatedPopoverWidthPx = 0

  get popoverWidthPx(): number {
    if (typeof this.popoverWidth === 'number') {
      return this.popoverWidth
    }

    if (this.popoverWidth === 'auto') {
      return this.autoCalculatedPopoverWidthPx
    }

    // value must match tailwindcss class values
    switch (this.popoverWidth) {
      case 'wide':
        // w-80
        return 320
      case 'default':
        // w-56
        return 224
      default:
        return 224
    }
  }

  popoverHeightPx = 0
  get willExceedViewportLeftEdge(): boolean {
    return this.popoverAnchor.left - this.popoverWidthPx < 8
  }

  get willExceedViewportRightEdge(): boolean {
    // currently popover modal is only used inside the post modal where it appears above a button and its left position is aligned with the button
    // => popover modal can only go out of bound to the right side of the viewport
    if (this.popoverPosition === 'top') {
      return this.popoverAnchor.left + this.popoverWidthPx + 8 > this.windowWidth
    }
    return this.popoverAnchor.left + this.popoverAnchor.width + this.popoverWidthPx + 32 > this.windowWidth
  }

  get willExceedViewportBottomEdge(): boolean {
    if (this.popoverPosition === 'side') {
      return this.popoverAnchor.top + this.popoverHeightPx + 32 > this.windowHeight
    }
    return false
  }

  get willExceedViewportTopEdge(): boolean {
    // `popoverAnchor.top - 12 (tip height)` gives us
    // the actual `top` value of the popover modal.
    return this.popoverAnchor.top - 12 - TOP_EDGE_MARGIN < this.popoverHeightPx
  }

  get isAnchorTooLow(): boolean {
    if (this.popoverPosition === 'side') {
      return this.windowHeight - this.popoverAnchor.top - this.popoverAnchor.height < 56
    }
    return false
  }

  get popoverSidePosition(): 'left' | 'right' {
    if ((!this.isRtl && this.willExceedViewportRightEdge) || (this.isRtl && !this.willExceedViewportLeftEdge))
      return 'left'
    return 'right'
  }

  get popoverPosition(): string {
    return this.popoverAnchor.position || 'top'
  }

  get popoverOnTopStyle(): Record<string, string> {
    const popoverLeft =
      this.popoverSidePosition === 'left'
        ? this.popoverAnchor.left + this.popoverAnchor.width / 2 - this.popoverWidthPx + 48
        : this.popoverAnchor.left + this.popoverAnchor.width / 2 - 48

    const styles = {
      // leave 12px for the tip + some space between the modal and the anchor
      bottom: `${this.anchorDistanceToBottom + 12}px`,
      left: `${popoverLeft}px`,
      width: this.popoverWidth === 'auto' ? 'auto' : `${this.popoverWidthPx}px`,
    }

    return this.willExceedViewportTopEdge ? { ...styles, top: `${TOP_EDGE_MARGIN}px` } : styles
  }

  get popoverToSideStyle(): Record<string, string> {
    if (this.isAnchorTooLow) {
      return this.popoverOnTopStyle
    }
    const popoverLeftToLeft = this.popoverAnchor.left - this.popoverWidthPx - 12
    const popoverLeftToRight = this.popoverAnchor.left + this.popoverAnchor.width + 12
    const popoverLeft = this.popoverSidePosition === 'left' ? popoverLeftToLeft : popoverLeftToRight
    const popoverTop = this.willExceedViewportBottomEdge
      ? this.windowHeight - this.popoverHeightPx - 32
      : this.popoverAnchor.top + this.popoverAnchor.height / 2 - 12 - 16

    return {
      top: `${popoverTop}px`,
      left: `${popoverLeft}px`,
      minHeight: '56px',
      width: this.popoverWidth === 'auto' ? 'auto' : `${this.popoverWidthPx}px`,
    }
  }

  get popoverStyle(): Record<string, string> {
    if (this.popoverPosition === 'top') {
      return this.popoverOnTopStyle
    }
    return this.popoverToSideStyle
  }

  get popoverOnTopTipStyle(): Record<string, string> {
    return {
      bottom: `${this.anchorDistanceToBottom - 12}px`,
      left: `${this.popoverAnchor.left + this.popoverAnchor.width / 2 - 12}px`,
    }
  }

  get popoverToSideTipStyle(): Record<string, string> {
    if (this.isAnchorTooLow) {
      return this.popoverOnTopTipStyle
    }
    if (this.popoverSidePosition === 'left') {
      return {
        top: `${this.popoverAnchor.top + this.popoverAnchor.height / 2 - 12}px`,
        left: `${this.popoverAnchor.left - 12}px`,
        transform: 'rotate(270deg)',
      }
    }
    return {
      top: `${this.popoverAnchor.top + this.popoverAnchor.height / 2 - 12}px`,
      left: `${this.popoverAnchor.left + this.popoverAnchor.width - 12}px`,
      transform: 'rotate(90deg)',
    }
  }
  get popoverTipStyle(): Record<string, string> {
    if (this.popoverPosition === 'top') {
      return this.popoverOnTopTipStyle
    }
    return this.popoverToSideTipStyle
  }

  mounted(): void {
    this.popoverHeightPx = this.$refs.popover.clientHeight
    this.autoCalculatedPopoverWidthPx = this.$refs.popover.clientWidth
  }

  cancel(): void {
    this.$emit('cancel')
  }
}
